import { Download, Loader2 } from "lucide-react";
import {
  useDataProvider,
  useGetResourceLabel,
  useListContext,
  useNotify,
  useStore,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { downloadExport } from "./excelWorkbook";
import { fetchAllRecords, fetchExportLookups } from "./fetchForExport";
import type { SheetColumn } from "./types";

const GROUP_ORDER: SheetColumn<unknown>["group"][] = [
  "identity",
  "prospecting",
  "contact",
  "address",
  "follow_up",
  "custom",
];

/**
 * « Exporter » button of a list: picks the columns, then downloads the rows
 * matching the list's filters as a formatted Excel file.
 */
export const ExcelExportButton = <RecordType extends { id: Identifier }>({
  columns,
  selectedOnly,
}: {
  columns: SheetColumn<RecordType>[];
  /** In the bulk actions bar: exports the selected rows only. */
  selectedOnly?: boolean;
}) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        className="cursor-pointer"
        onClick={() => setOpen(true)}
      >
        <Download />
        {translate("crm.spreadsheet.export.button")}
      </Button>
      {open && (
        <ExcelExportDialog
          columns={columns}
          selectedOnly={selectedOnly}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

const ExcelExportDialog = <RecordType extends { id: Identifier }>({
  columns,
  selectedOnly,
  onClose,
}: {
  columns: SheetColumn<RecordType>[];
  selectedOnly?: boolean;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const getResourceLabel = useGetResourceLabel();
  const { currency } = useConfigurationContext();
  const { resource, filterValues, sort, total, selectedIds } =
    useListContext<RecordType>();
  const filter = selectedOnly
    ? { "id@in": `(${selectedIds.join(",")})` }
    : filterValues;
  const rowCount = selectedOnly ? selectedIds.length : (total ?? 0);
  // Unchecked columns are remembered, so a field added later is exported by default.
  const [excluded, setExcluded] = useStore<string[]>(
    `export.${resource}.excludedColumns`,
    [],
  );
  const [isExporting, setExporting] = useState(false);
  const selected = columns.filter((column) => !excluded.includes(column.key));

  const toggle = (key: string, checked: boolean) =>
    setExcluded(
      checked ? excluded.filter((item) => item !== key) : [...excluded, key],
    );

  const handleExport = async () => {
    setExporting(true);
    try {
      const records = await fetchAllRecords<RecordType>(
        dataProvider,
        resource,
        filter,
        sort,
      );
      const lookups = await fetchExportLookups(dataProvider, records);
      const label = getResourceLabel(resource, 2);
      await downloadExport({
        fileName: `opticercle-${slug(label)}-${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName: label,
        columns: selected,
        records,
        lookups,
        currency,
      });
      onClose();
    } catch (error) {
      console.error("Excel export failed", error);
      notify("crm.spreadsheet.export.error", { type: "error" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-9/10 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{translate("crm.spreadsheet.export.title")}</DialogTitle>
          <DialogDescription>
            {translate("crm.spreadsheet.export.description", {
              smart_count: rowCount,
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setExcluded([])}>
            {translate("crm.spreadsheet.export.select_all")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExcluded(columns.map((column) => column.key))}
          >
            {translate("crm.spreadsheet.export.select_none")}
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
          {GROUP_ORDER.map((group) => {
            const groupColumns = columns.filter(
              (column) => column.group === group,
            );
            if (!groupColumns.length) return null;
            return (
              <fieldset key={group} className="flex flex-col gap-2">
                <legend className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {translate(`crm.spreadsheet.groups.${group}`)}
                </legend>
                {groupColumns.map((column) => {
                  const id = `export-column-${column.key}`;
                  return (
                    <div key={column.key} className="flex items-center gap-2">
                      <Checkbox
                        id={id}
                        checked={!excluded.includes(column.key)}
                        onCheckedChange={(checked) =>
                          toggle(column.key, checked === true)
                        }
                      />
                      <Label
                        htmlFor={id}
                        className="font-normal cursor-pointer"
                      >
                        {column.header}
                      </Label>
                    </div>
                  );
                })}
              </fieldset>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExporting || selected.length === 0}
          >
            {isExporting ? <Loader2 className="animate-spin" /> : <Download />}
            {translate("crm.spreadsheet.export.submit", {
              smart_count: selected.length,
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const slug = (text: string) =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
